import Cookies from 'js-cookie';

// Dynamically determine API URL based on current hostname
// Use environment variable if set, otherwise construct from current location
const getApiBaseUrl = (): string => {
  // First, check if environment variable is set (highest priority)
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  
  // Check if we're in the browser
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // For localhost, use localhost:8000
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    
    // For production domains, use HTTPS and api subdomain
    // If on portal.takhzeeni.com, use api.takhzeeni.com
    if (hostname.includes('takhzeeni.com')) {
      return `${protocol}//api.takhzeeni.com`;
    }
    
    // For other domains, use same protocol and hostname with port 8000
    return `${protocol}//${hostname}:8000`;
  }
  
  // Fallback to localhost for server-side rendering
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ServerNotRespondingError extends Error {
  constructor() {
    super('Server not responding');
    this.name = 'ServerNotRespondingError';
  }
}

/**
 * Thrown when the FastAPI ``active_contract_gate`` middleware rejects a
 * request because the authenticated customer has no active rental contract.
 * The fetch helper also auto-navigates to /no-active-contract; the error
 * is rethrown so individual call sites can short-circuit their own
 * follow-up logic instead of silently rendering empty data.
 */
export class NoActiveContractError extends Error {
  redirect: string;
  constructor(message = 'No active rental contract', redirect = '/no-active-contract') {
    super(message);
    this.name = 'NoActiveContractError';
    this.redirect = redirect;
  }
}

const NO_ACTIVE_CONTRACT_PATH = '/no-active-contract';

function redirectToNoActiveContract(target: string): void {
  if (typeof window === 'undefined') return;
  // Avoid loops if we're already on (or navigating to) the gate page,
  // or on an auth page that should never be gated.
  const here = window.location.pathname;
  if (
    here === target ||
    here.startsWith('/login') ||
    here.startsWith('/signup') ||
    here.startsWith('/forgot-password') ||
    here.startsWith('/reset-password')
  ) {
    return;
  }
  // Use a hard navigation so any in-flight React state from gated pages
  // is discarded; the user lands on a clean /no-active-contract render.
  window.location.href = target;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const errorCode = (data as { error_code?: string } | null)?.error_code;
    if (response.status === 403 && errorCode === 'NO_ACTIVE_CONTRACT') {
      const redirectTo =
        (data as { redirect?: string } | null)?.redirect ||
        NO_ACTIVE_CONTRACT_PATH;
      redirectToNoActiveContract(redirectTo);
      throw new NoActiveContractError(
        (data as { detail?: string } | null)?.detail || 'No active contract',
        redirectTo,
      );
    }
    throw new ApiError(
      (data as { message?: string } | null)?.message ||
        `HTTP error ${response.status}`,
      response.status,
      data,
    );
  }
  return response.json();
}

async function fetchWithErrorHandling<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include', // Send cookies with requests
      cache: 'no-store', // Disable caching - always fetch fresh data
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options?.headers,
      },
    });
    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError || error instanceof NoActiveContractError) {
      throw error;
    }
    // Network error or server not responding
    throw new ServerNotRespondingError();
  }
}

// ===== Shared types for dispute mandates / QA templates =====
// Mirrored from the Odoo serialisers so list / detail / mutation
// responses share one shape and the React forms can stay simple.

export interface DisputeMandateRulePayload {
  id?: number;
  sequence: number;
  dispute_type: string;
  mandate_granted: boolean;
  threshold_type: string;
  threshold_value: number;
  allowed_action: string;
}

export interface DisputeMandatePayload {
  id: string;
  name: string;
  state: string;
  active: boolean;
  is_valid: boolean;
  client_id: number | null;
  client_name: string;
  contract_id: number | null;
  contract_name: string;
  effective_date: string | null;
  expiry_date: string | null;
  authorized_signatory: string;
  signatory_title: string;
  signatory_email: string;
  digital_approval: boolean;
  approval_date: string | null;
  approved_by_id: number | null;
  approved_by_name: string;
  global_shipment_threshold: number;
  global_shipment_threshold_currency_id: number | null;
  global_shipment_threshold_currency_name: string;
  global_sku_threshold: number;
  global_sku_threshold_currency_id: number | null;
  global_sku_threshold_currency_name: string;
  client_response_sla_hours: number;
  default_action: string;
  notes: string;
  created_at: string | null;
  updated_at: string | null;
  rules: DisputeMandateRulePayload[];
  rule_count: number;
}

export type DisputeMandateInput = Partial<{
  contract_id: number | null;
  effective_date: string;
  expiry_date: string | null;
  authorized_signatory: string;
  signatory_title: string;
  signatory_email: string;
  global_shipment_threshold: number;
  global_shipment_threshold_currency_id: number | null;
  global_sku_threshold: number;
  global_sku_threshold_currency_id: number | null;
  client_response_sla_hours: number;
  default_action: string;
  notes: string;
  active: boolean;
  rules: DisputeMandateRulePayload[];
}>;

export interface QaTemplateLinePayload {
  id?: number;
  sequence: number;
  check_type: string;
  sampling_method: string;
  sample_size: number;
  is_mandatory: boolean;
  instructions: string;
}

export interface QaTemplatePayload {
  id: string;
  name: string;
  sequence: number;
  active: boolean;
  is_default: boolean;
  client_id: number | null;
  client_name: string;
  product_category_id: number | null;
  product_category_name: string;
  notes: string;
  lines: QaTemplateLinePayload[];
  line_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export type QaTemplateInput = Partial<{
  name: string;
  sequence: number;
  active: boolean;
  product_category_id: number | null;
  notes: string;
  lines: QaTemplateLinePayload[];
}>;

// ===== Dispute detail =====
// Detail payload is a superset of the list row (so the same React
// summary card can render both); keep them aligned with Odoo's
// ``_serialize_dispute`` serialiser.

export interface DisputeAttachmentPayload {
  id: number;
  name: string;
  mimetype: string;
  file_size: number;
  url: string;
}

export interface DisputeDetailPayload {
  id: string;
  reference: string;
  state: string;
  state_label: string;
  active: boolean;
  is_locked: boolean;
  // Classification
  dispute_type: string;
  dispute_type_label: string;
  severity: string;
  severity_label: string;
  trigger_source: string;
  trigger_source_label: string;
  inspection_method: string;
  // Linked records
  asn_id: string;
  asn_reference: string;
  receiving_id: string;
  receiving_reference: string;
  shipment_reference: string;
  // Product
  product_id: string;
  product_name: string;
  product_barcode: string;
  lot_name: string;
  // Quantities
  asn_qty: number;
  actual_qty: number;
  variance_qty: number;
  variance_percentage: number;
  // Value
  unit_value: number;
  disputed_value: number;
  currency: string;
  // SLA
  sla_status: string;
  sla_status_label: string;
  sla_breached: boolean;
  sla_deadline: string | null;
  sla_hours: number;
  escalated: boolean;
  // Resolution
  resolution_action: string;
  resolution_action_label: string;
  resolution_notes: string;
  resolved_date: string | null;
  resolved_by_name: string;
  suggested_action: string;
  suggested_action_label: string;
  can_auto_resolve: boolean;
  mandate_evaluation_result: string;
  mandate_evaluation_label: string;
  mandate_evaluation_notes: string;
  mandate_id: number | null;
  mandate_name: string;
  // Client side
  client_notified_date: string | null;
  client_response_date: string | null;
  client_decision: string;
  client_notes: string;
  // Description / evidence
  description: string;
  attachments: DisputeAttachmentPayload[];
  attachment_count: number;
  photo_required: boolean;
  photo_provided: boolean;
  // Options for the resolution-action dropdown the portal renders
  // on the approve modal.
  resolution_action_options: Array<{ key: string; label: string }>;
  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

export const api = {
  // Health check
  healthCheck: () => fetchWithErrorHandling<{ status: string }>('/api/health'),

  // Active-contract gate status. Used by /no-active-contract's "Check
  // again" button so the user can self-detect when an admin activates
  // their contract without reloading the entire portal.
  getContractStatus: () =>
    fetchWithErrorHandling<{
      has_active: boolean;
      active_count: number;
      total_count: number;
      error?: string;
    }>('/api/customer/contract-status'),

  // Auth
  login: (email: string, password: string) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      user?: {
        id: string;
        email: string;
        name: string;
        company_name: string;
        vat_number: string;
        profile_image?: string;
      };
      token?: string;
      requires_password_setup?: boolean;  // True if user logged in with OTP and needs to set new password
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  forgotPassword: (email: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      '/api/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    ),

  resetPassword: (email: string, token: string, newPassword: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      '/api/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ email, token, new_password: newPassword }),
      }
    ),

  signupRequest: (email: string, customerId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      '/api/auth/signup-request',
      {
        method: 'POST',
        body: JSON.stringify({ email, customer_id: customerId }),
      }
    ),

  signup: (email: string, password: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      '/api/auth/signup',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    ),

  // Set new password after OTP verification
  setNewPassword: (email: string, newPassword: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      '/api/auth/set-password',
      {
        method: 'POST',
        body: JSON.stringify({ email, new_password: newPassword }),
      }
    ),

  // Features
  getFeatures: () =>
    fetchWithErrorHandling<{
      show_completed_payments: boolean;
      enable_wallet: boolean;
      enable_advance_payment: boolean;
      enable_partial_payment: boolean;
      show_stock_alerts: boolean;
      enable_correspondence: boolean;
      enable_contract_download: boolean;
    }>('/api/features'),

  // Dashboard
  getDashboard: () =>
    fetchWithErrorHandling<{
      dashboard: {
        user_id: string;
        stats: {
          total_contracts: number;
          active_contracts: number;
          pending_payments: number;
          pending_amount: number;
          total_inventory_items: number;
          total_inventory_quantity: number;
          storage_used: number;
          // P0: ``storage_limit`` is null when the customer's active
          // contract uses mixed billing units or a single non-cbm
          // unit (see contracts endpoint).
          storage_limit: number | null;
          storage_unit: string | null;
          mixed_units?: boolean;
          total_space_known?: boolean;
          open_invoices: number;
          overdue_invoices: number;
          upcoming_payments: number;
          low_stock_items: number;
          outstanding_balance?: number;
          currency: string;
        };
        recent_activity: Array<{
          id: string;
          type: string;
          title: string;
          description: string;
          date: string;
          icon: string;
          link?: string | null;
        }>;
        charts: {
          // P0: ``limit`` is null when storage_limit is null.
          storage_trend: Array<{ month: string; used: number; limit: number | null }>;
          payment_history: Array<{ month: string; amount: number }>;
          inventory_by_category: Array<{ category: string; quantity: number }>;
        };
      };
    }>('/api/dashboard'),

  getDashboardActivities: (params: { limit?: number; offset?: number } = {}) => {
    const limit = params.limit ?? 15;
    const offset = params.offset ?? 0;
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return fetchWithErrorHandling<{
      activities: Array<{
        id: string;
        type: string;
        title: string;
        description: string;
        date: string;
        icon: string;
        link?: string | null;
      }>;
      has_more: boolean;
      limit: number;
      offset: number;
    }>(`/api/dashboard/activities?${qs.toString()}`);
  },

  // Company
  getCompany: () =>
    fetchWithErrorHandling<{
      id: number | null;
      name: string;
      logo_url: string | null;
      logo: string | null;
      email?: string;
      phone?: string;
      website?: string;
    }>('/api/company'),

  // Contracts.
  // P0 (D2/B3): ``total_space`` / ``storage_limit`` / ``storage_available``
  // / ``full_volume`` are ``null`` when the source contract's rental
  // lines use mixed billing units or a single non-cbm unit. Callers
  // must guard rendering with ``fmtSpace`` (see contracts/page.tsx)
  // instead of assuming a numeric value.
  getContracts: () =>
    fetchWithErrorHandling<{
      contracts: Array<{
        id: string;
        contract_number: string;
        user_id: string;
        start_date: string;
        end_date: string;
        status: string;
        total_space: number | null;
        storage_limit?: number | null;
        storage_used: number;
        storage_available?: number | null;
        storage_unit: string | null;
        full_volume?: number | null;
        mixed_units?: boolean;
        annual_value: number;
        currency: string;
        pdf_url: string;
        terms: string;
      }>;
    }>('/api/contracts'),

  // Download contract as PDF
  downloadContract: async (contractId: string): Promise<Blob> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contracts/${contractId}/download`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-User-Email': Cookies.get('user_email') || '',
          'X-User-CustomerID': Cookies.get('customer_id') || '',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new ApiError(`Failed to download contract: ${errorText}`, response.status);
      }
      
      return response.blob();
    } catch (error) {
      console.error('Download contract error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ServerNotRespondingError();
    }
  },

  // Download invoice as PDF
  downloadInvoice: async (invoiceId: string): Promise<Blob> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/download`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-User-Email': Cookies.get('user_email') || '',
          'X-User-CustomerID': Cookies.get('customer_id') || '',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Invoice download failed:', response.status, errorText);
        throw new ApiError(`Failed to download invoice: ${errorText}`, response.status);
      }

      return response.blob();
    } catch (error) {
      console.error('Download invoice error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ServerNotRespondingError();
    }
  },

  // Invoices
  getInvoices: () =>
    fetchWithErrorHandling<{
      invoices: Array<{
        id: string;
        invoice_number: string;
        user_id: string;
        amount: number;
        currency: string;
        due_date: string;
        status: string;
        description: string;
        created_at: string;
        paid_date?: string;
      }>;
    }>('/api/invoices'),

  getOpenInvoices: () =>
    fetchWithErrorHandling<{
      invoices: Array<{
        id: string;
        invoice_number: string;
        amount: number;
        currency: string;
        due_date: string;
        status: string;
        description: string;
      }>;
    }>('/api/invoices/open'),

  getUpcomingInvoices: () =>
    fetchWithErrorHandling<{
      invoices: Array<{
        id: string;
        invoice_number: string;
        amount: number;
        currency: string;
        due_date: string;
        status: string;
        description: string;
      }>;
    }>('/api/invoices/upcoming'),

  getPaidInvoices: () =>
    fetchWithErrorHandling<{
      invoices: Array<{
        id: string;
        invoice_number: string;
        amount: number;
        currency: string;
        due_date: string;
        status: string;
        description: string;
        paid_date?: string;
      }>;
    }>('/api/invoices/paid'),

  // Payments
  getPayments: () =>
    fetchWithErrorHandling<{
      payments: Array<{
        id: string;
        user_id: string;
        invoice_id: string;
        amount: number;
        currency: string;
        payment_date: string;
        payment_method: string;
        reference: string;
        status: string;
        card_last_four?: string;
      }>;
    }>('/api/payments'),

  processPayment: (data: {
    invoice_id: string;
    amount: number;
    payment_method: string;
  }) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      transaction_id?: string;
    }>('/api/payments/process', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Wallet
  getWallet: () =>
    fetchWithErrorHandling<{
      wallet: {
        user_id: string;
        balance: number;
        currency: string;
        auto_deduction_enabled: boolean;
        last_top_up: string;
        last_top_up_amount: number;
        transactions: Array<{
          id: string;
          type: string;
          amount: number;
          date: string;
          description: string;
        }>;
      };
    }>('/api/wallet'),

  // Inventory
  getInventoryMovements: () =>
    fetchWithErrorHandling<{
      movements: Array<{
        id: string;
        user_id: string;
        product_id: string;
        product_name: string;
        quantity: number;
        type: string;
        date: string;
        reference: string;
      }>;
    }>('/api/inventory-movements'),

  getProducts: () =>
    fetchWithErrorHandling<{
      products: Array<{
        id: string;
        barcode: string;
        name: string;
        quantity: number;
        threshold: number;
        unit: string;
        category: string;
        expiry_date: string | null;
        location: string;
        cost_price: number;
        selling_price: number;
        supplier: string;
        description: string;
      }>;
    }>('/api/products'),

  getProductStats: () =>
    fetchWithErrorHandling<{
      total_skus: number;
      in_stock: number;
      out_of_stock: number;
      low_stock: number;
      total_value: number;
      has_hidden_untracked_stock?: boolean;
    }>('/api/products/stats'),

  getProduct: (productId: string) =>
    fetchWithErrorHandling<{
      id: string;
      barcode: string;
      name: string;
      quantity: number;
      threshold: number;
      unit: string;
      category: string;
      expiry_date: string | null;
      location: string;
      cost_price: number;
      selling_price: number;
      supplier: string;
      description: string;
      image_url?: string;
      created_at?: string;
      created_by?: string;
      status?: string;
      category_id?: string;
      unit_id?: string;
    }>(`/api/products/${productId}`),

  createProduct: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{
      id: string;
      barcode: string;
      name: string;
      quantity: number;
      threshold: number;
      unit: string;
      category: string;
      expiry_date: string | null;
      location: string;
      cost_price: number;
      selling_price: number;
      supplier: string;
      description: string;
      image_url?: string;
      created_at?: string;
      created_by?: string;
      status?: string;
    }>('/api/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProduct: (productId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/products/${productId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  updateProductThreshold: (productId: string, threshold: number) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/products/${productId}/threshold`,
      {
        method: 'PATCH',
        body: JSON.stringify({ threshold }),
      }
    ),

  // Disputes (inbound receiving disputes)
  getDisputeList: (params?: { state?: string; sla_breached?: boolean }) => {
    const search = new URLSearchParams();
    if (params?.state && params.state !== 'all') search.set('state', params.state);
    if (params?.sla_breached === true) search.set('sla_breached', 'true');
    const qs = search.toString();
    return fetchWithErrorHandling<{
      disputes: Array<{
        id: string;
        reference: string;
        state: string;
        state_label: string;
        dispute_type: string;
        dispute_type_label: string;
        severity: string;
        severity_label: string;
        asn_reference: string;
        asn_id: string;
        receiving_reference: string;
        receiving_id: string;
        shipment_reference: string;
        product_id: string;
        product_name: string;
        product_barcode: string;
        asn_qty: number;
        actual_qty: number;
        variance_qty: number;
        variance_percentage: number;
        unit_value: number;
        disputed_value: number;
        currency: string;
        sla_status: string;
        sla_status_label: string;
        sla_breached: boolean;
        sla_deadline: string | null;
        sla_hours: number;
        escalated: boolean;
        client_notified_date: string | null;
        client_response_date: string | null;
        created_at: string | null;
        updated_at: string | null;
        resolution_action: string;
        resolved_date: string | null;
        attachment_count: number;
        photo_required: boolean;
        photo_provided: boolean;
        description: string;
      }>;
      total: number;
      state_labels: Record<string, string>;
      dispute_type_labels: Record<string, string>;
      severity_labels: Record<string, string>;
      sla_status_labels: Record<string, string>;
    }>(`/api/disputes${qs ? `?${qs}` : ''}`);
  },

  getDisputeDetail: (disputeId: string | number) =>
    fetchWithErrorHandling<{ dispute: DisputeDetailPayload }>(
      `/api/disputes/${disputeId}`,
    ),

  clientApproveDispute: (
    disputeId: string | number,
    data: { resolution_action?: string; client_notes?: string } = {},
  ) =>
    fetchWithErrorHandling<{ success: boolean; dispute: DisputeDetailPayload }>(
      `/api/disputes/${disputeId}/client-approve`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  clientRejectDispute: (
    disputeId: string | number,
    data: { client_notes?: string } = {},
  ) =>
    fetchWithErrorHandling<{ success: boolean; dispute: DisputeDetailPayload }>(
      `/api/disputes/${disputeId}/client-reject`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  // ===== Dispute Mandates =====
  // The mandate form is rich enough that we share one shape between
  // list / detail / create / update responses to keep the React forms
  // simple — every endpoint always returns the full `MandatePayload`.
  getDisputeMandateOptions: () =>
    fetchWithErrorHandling<{
      states: Array<{ key: string; label: string }>;
      default_actions: Array<{ key: string; label: string }>;
      dispute_types: Array<{ key: string; label: string }>;
      threshold_types: Array<{ key: string; label: string }>;
      allowed_actions: Array<{ key: string; label: string }>;
      contracts: Array<{ id: number; name: string; date_order: string | null }>;
      currencies: Array<{ id: number; name: string; symbol: string }>;
      default_currency_id: number | null;
      client: { id: number; name: string } | null;
    }>('/api/dispute-mandates/options'),

  getDisputeMandateList: () =>
    fetchWithErrorHandling<{
      mandates: DisputeMandatePayload[];
      total: number;
    }>('/api/dispute-mandates'),

  getDisputeMandateDetail: (mandateId: string | number) =>
    fetchWithErrorHandling<{
      mandate: DisputeMandatePayload;
    }>(`/api/dispute-mandates/${mandateId}`),

  createDisputeMandate: (data: DisputeMandateInput) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      '/api/dispute-mandates',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  updateDisputeMandate: (mandateId: string | number, data: DisputeMandateInput) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      `/api/dispute-mandates/${mandateId}`,
      { method: 'PUT', body: JSON.stringify(data) }
    ),

  submitDisputeMandate: (mandateId: string | number) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      `/api/dispute-mandates/${mandateId}/submit`,
      { method: 'POST' }
    ),

  cancelDisputeMandate: (mandateId: string | number) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      `/api/dispute-mandates/${mandateId}/cancel`,
      { method: 'POST' }
    ),

  resetDisputeMandate: (mandateId: string | number) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      `/api/dispute-mandates/${mandateId}/reset`,
      { method: 'POST' }
    ),

  createDefaultRulesDisputeMandate: (mandateId: string | number) =>
    fetchWithErrorHandling<{ success: boolean; mandate: DisputeMandatePayload }>(
      `/api/dispute-mandates/${mandateId}/create-default-rules`,
      { method: 'POST' }
    ),

  // ===== QA Templates =====
  getQaTemplateOptions: () =>
    fetchWithErrorHandling<{
      check_types: Array<{ key: string; label: string }>;
      sampling_methods: Array<{ key: string; label: string }>;
      product_categories: Array<{ id: number; name: string }>;
      client: { id: number; name: string } | null;
    }>('/api/qa-templates/options'),

  getQaTemplateList: () =>
    fetchWithErrorHandling<{
      templates: QaTemplatePayload[];
      total: number;
      partner_id: number;
    }>('/api/qa-templates'),

  getQaTemplateDetail: (templateId: string | number) =>
    fetchWithErrorHandling<{
      template: QaTemplatePayload;
      is_editable: boolean;
      is_owner: boolean;
    }>(`/api/qa-templates/${templateId}`),

  createQaTemplate: (data: QaTemplateInput) =>
    fetchWithErrorHandling<{ success: boolean; template: QaTemplatePayload }>(
      '/api/qa-templates',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  updateQaTemplate: (templateId: string | number, data: QaTemplateInput) =>
    fetchWithErrorHandling<{ success: boolean; template: QaTemplatePayload }>(
      `/api/qa-templates/${templateId}`,
      { method: 'PUT', body: JSON.stringify(data) }
    ),

  deleteQaTemplate: (templateId: string | number) =>
    fetchWithErrorHandling<{ success: boolean }>(
      `/api/qa-templates/${templateId}`,
      { method: 'DELETE' }
    ),

  // ASN (Advance Shipment Notice)
  getASNList: () =>
    fetchWithErrorHandling<{
      asn_records: Array<{
        id: string;
        reference: string;
        partner_id?: string;
        partner_name?: string;
        // Odoo returns ``status`` (mapped from ASN.state) for the list.
        status: string;
        expected_date: string;
        // ``supplier_name`` is what the Odoo controller emits;
        // ``supplier`` is the historical alias kept so callers that
        // never updated still work.
        supplier_name?: string;
        supplier?: string;
        supplier_id?: string;
        notes: string;
        products: Array<{
          product_id: string;
          product_name: string;
          expected_qty: number;
        }>;
        product_count?: number;
        created_at: string;
        updated_at: string;
        portal_user_email?: string;
        approved_by: string | null;
        approved_date?: string | null;
        // Receiving state surfaced so the list/split-view badges can
        // show the effective status (e.g. ``Dispute``) when the
        // warehouse side has moved off the happy path.
        receiving_state?: string;
        receiving_state_label?: string;
        receiving_is_exception?: boolean;
      }>;
    }>('/api/asn'),

  createASN: (data: {
    expected_date: string;
    supplier: string;
    notes: string;
    products: Array<{ product_id: string; product_name: string; expected_qty: number }>;
  }) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      asn?: { id: string; reference: string };
    }>('/api/asn', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getASNDetail: (asnId: string) =>
    fetchWithErrorHandling<{
      id: string;
      reference: string;
      partner_id: string;
      partner_name: string;
      status: string;
      expected_date: string;
      supplier_name: string;
      notes: string;
      products: Array<{
        id: string;
        product_id: string;
        product_name: string;
        expected_qty: number;
        notes: string;
      }>;
      product_count: number;
      created_at: string;
      updated_at: string;
      portal_user_email: string;
      approved_by: string | null;
      approved_date: string | null;
      receiving: {
        id: string;
        reference: string;
        state: string;
        state_label: string;
        state_labels: Record<string, string>;
        happy_path: string[];
        exception_states: string[];
        is_exception: boolean;
        expected_time: string | null;
        arrival_time: string | null;
        created_at: string | null;
        updated_at: string | null;
        history: Array<{
          from: string;
          from_label: string;
          to: string;
          to_label: string;
          date: string | null;
          author: string;
          is_creation?: boolean;
        }>;
      } | null;
    }>(`/api/asn/${asnId}`),

  updateASN: (asnId: string, data: {
    expected_date?: string;
    supplier?: string;
    notes?: string;
    products?: Array<{ product_id: string; expected_qty: number }>;
  }) =>
    fetchWithErrorHandling<{
      success: boolean;
      asn: {
        id: string;
        reference: string;
        status: string;
        expected_date: string;
        supplier_name: string;
        notes: string;
        products: Array<{
          id: string;
          product_id: string;
          product_name: string;
          expected_qty: number;
        }>;
      };
    }>(`/api/asn/${asnId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateASNStatus: (asnId: string, status: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/asn/${asnId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    ),

  submitASNForReview: (asnId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; status: string }>(
      `/api/asn/${asnId}/submit`,
      {
        method: 'POST',
      }
    ),

  cancelASN: (asnId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; status: string }>(
      `/api/asn/${asnId}/cancel`,
      {
        method: 'POST',
      }
    ),

  approveASN: (asnId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; status: string }>(
      `/api/asn/${asnId}/approve`,
      {
        method: 'POST',
      }
    ),

  // Receiving (Stock Receiving)
  getReceivingDetail: (receivingId: string) =>
    fetchWithErrorHandling<{
      receiving: {
        id: number;
        reference: string;
        date: string;
        status: string;
        warehouse: string;
        notes: string;
        products: Array<{
          id: number;
          name: string;
          barcode: string;
          quantity: number;
        }>;
        created_at: string;
      };
    }>(`/api/receiving/${receivingId}`),

  // Dispatch Requests
  getDispatchList: () =>
    fetchWithErrorHandling<{
      dispatch_requests: Array<{
        id: string;
        reference: string;
        user_id: string;
        status: string;
        requested_date: string;
        dispatched_date: string | null;
        delivery_address: string;
        priority: string;
        notes: string;
        products: Array<{
          product_id: string;
          product_name: string;
          requested_qty: number;
          dispatched_qty: number;
        }>;
        attachments: Array<{
          id: string;
          filename: string;
          type: string;
          size: number;
          url: string;
        }>;
        created_at: string;
        updated_at: string;
        created_by: string;
        approved_by: string | null;
        approved_at: string | null;
      }>;
    }>('/api/dispatch'),

  createDispatch: (data: {
    requested_date: string;
    delivery_address: string;
    priority: string;
    notes: string;
    products: Array<{ product_id: string; product_name: string; requested_qty: number }>;
  }) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      dispatch?: { id: string; reference: string };
    }>('/api/dispatch', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDispatchStatus: (dispatchId: string, status: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/dispatch/${dispatchId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    ),

  // Correspondence
  getCorrespondence: () =>
    fetchWithErrorHandling<{
      correspondence: Array<{
        id: string;
        user_id: string;
        subject: string;
        from: string;
        to: string;
        date: string;
        preview: string;
        content: string;
        read: boolean;
        type: 'sent' | 'received';
        attachments: Array<{
          id: string;
          filename: string;
          type: string;
          size: number;
          url: string;
        }>;
      }>;
    }>('/api/correspondence'),

  sendCorrespondence: (data: {
    user_id: string;
    from: string;
    to: string;
    subject: string;
    body: string;
    attachments: Array<{
      id: string;
      filename: string;
      type: string;
      size: number;
      url: string;
    }>;
  }) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      correspondence?: {
        id: string;
        subject: string;
        from: string;
        to: string;
        date: string;
        preview: string;
        content: string;
        read: boolean;
        type: 'sent';
        attachments: Array<{
          id: string;
          filename: string;
          type: string;
          size: number;
          url: string;
        }>;
      };
    }>('/api/correspondence', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Addresses
  getAddresses: () =>
    fetchWithErrorHandling<{
      addresses: Array<{
        id: string;
        user_id: string;
        address: string;
        type: string;
        is_default: boolean;
        label: string;
      }>;
    }>('/api/addresses'),

  addAddress: (data: { address: string; type: string; label: string }) =>
    fetchWithErrorHandling<{
      success: boolean;
      message: string;
      address?: { id: string };
    }>('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAddress: (
    addressId: string,
    data: { address?: string; type?: string; is_default?: boolean; label?: string }
  ) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/addresses/${addressId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  deleteAddress: (addressId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/addresses/${addressId}`,
      {
        method: 'DELETE',
      }
    ),

  // Suppliers
  getSuppliers: () =>
    fetchWithErrorHandling<{
      suppliers: Array<{
        id: string;
        name: string;
        code: string;
        contact_person: string;
        email: string;
        phone: string;
        address: string;
        website: string;
        products_count: number;
        total_skus: number;
        total_quantity: number;
        created_at: string;
        status: 'active' | 'inactive';
      }>;
    }>('/api/suppliers'),

  createSupplier: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{
      id: string;
      name: string;
      code: string;
      contact_person: string;
      email: string;
      phone: string;
      address: string;
      website: string;
      products_count: number;
      created_at: string;
      status: 'active' | 'inactive';
    }>('/api/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSupplier: (supplierId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/suppliers/${supplierId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Categories
  getCategories: () =>
    fetchWithErrorHandling<{
      categories: Array<{
        id: string;
        name: string;
        code: string;
        description: string;
        parent_id: string | null;
        parent_name: string | null;
        products_count: number;
        total_skus: number;
        total_quantity: number;
        created_at: string;
        status: 'active' | 'inactive';
      }>;
    }>('/api/categories'),

  createCategory: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{
      id: string;
      name: string;
      code: string;
      description: string;
      parent_id: string | null;
      parent_name: string | null;
      products_count: number;
      created_at: string;
      status: 'active' | 'inactive';
    }>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCategory: (categoryId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/categories/${categoryId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Units
  getUnits: () =>
    fetchWithErrorHandling<{
      units: Array<{
        id: string;
        name: string;
        symbol: string;
        category: string;
        parent_id: string | null;
        parent_name: string | null;
        conversion_ratio: number;
        products_count: number;
        total_skus: number;
        total_quantity: number;
        created_at: string;
        status: 'active' | 'inactive';
      }>;
    }>('/api/units'),

  createUnit: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{
      id: string;
      name: string;
      symbol: string;
      category: string;
      parent_id: string | null;
      parent_name: string | null;
      conversion_ratio: number;
      products_count: number;
      created_at: string;
      status: 'active' | 'inactive';
    }>('/api/units', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUnit: (unitId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/units/${unitId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Product-Supplier Junction (Many2Many Relationship)
  getProductSuppliers: (filters?: { product_id?: string; supplier_id?: string }) => {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', filters.product_id);
    if (filters?.supplier_id) params.append('supplier_id', filters.supplier_id);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling<{
      product_suppliers: Array<{
        id: string;
        product_id: string;
        product_name: string;
        product_barcode: string;
        supplier_id: string;
        supplier_name: string;
        supplier_code: string;
        quantity_provided: number;
        price: number;
        currency: string;
        lead_time: number;
        min_order_qty: number;
        is_preferred: boolean;
        last_order_date?: string;
        created_at: string;
      }>;
      summary: {
        total_records: number;
        total_quantity: number;
        total_value: number;
      };
    }>(`/api/product-suppliers${query}`);
  },

  getProductSupplier: (psId: string) =>
    fetchWithErrorHandling<{
      id: string;
      product_id: string;
      product_name: string;
      product_barcode: string;
      supplier_id: string;
      supplier_name: string;
      supplier_code: string;
      quantity_provided: number;
      price: number;
      currency: string;
      lead_time: number;
      min_order_qty: number;
      is_preferred: boolean;
      last_order_date?: string;
      created_at: string;
    }>(`/api/product-suppliers/${psId}`),

  createProductSupplier: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{
      id: string;
      product_id: string;
      supplier_id: string;
    }>('/api/product-suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProductSupplier: (psId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-suppliers/${psId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  deleteProductSupplier: (psId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-suppliers/${psId}`,
      {
        method: 'DELETE',
      }
    ),

  // Warehouse Suppliers (M2M with Products)
  getClientsSuppliers: () =>
    fetchWithErrorHandling<{
      suppliers: Array<{
        id: string;
        name: string;
        code: string;
        contact_person: string;
        email: string;
        phone: string;
        website: string;
        address: string;
        notes: string;
        status: 'active' | 'inactive';
        products_count: number;
        created_at: string;
      }>;
    }>('/api/warehouse-suppliers'),

  getAllClientsSuppliers: () =>
    fetchWithErrorHandling<{
      suppliers: Array<{
        id: string;
        name: string;
        code: string;
      }>;
    }>('/api/warehouse-suppliers/all'),

  createClientsSupplier: (data: { name: string; contact_person?: string; email?: string; phone?: string; website?: string; address?: string; notes?: string }) =>
    fetchWithErrorHandling<{
      success: boolean;
      id: string;
      name: string;
      code: string;
    }>('/api/warehouse-suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getClientsSupplier: (supplierId: string) =>
    fetchWithErrorHandling<{
      id: string;
      name: string;
      code: string;
      contact_person: string;
      email: string;
      phone: string;
      website: string;
      address: string;
      notes: string;
      status: 'active' | 'inactive';
      products: Array<{
        id: string;
        name: string;
        barcode: string;
        category: string;
      }>;
      products_count: number;
    }>(`/api/warehouse-suppliers/${supplierId}`),

  updateClientsSupplier: (supplierId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/warehouse-suppliers/${supplierId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  addProductToClientsSupplier: (supplierId: string, productId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/warehouse-suppliers/${supplierId}/products`,
      {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      }
    ),

  removeProductFromClientsSupplier: (supplierId: string, productId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/warehouse-suppliers/${supplierId}/products/${productId}`,
      {
        method: 'DELETE',
      }
    ),

  getProductClientsSuppliers: (productId: string) =>
    fetchWithErrorHandling<{
      suppliers: Array<{
        id: string;
        name: string;
        code: string;
        email: string;
        phone: string;
      }>;
    }>(`/api/products/${productId}/warehouse-suppliers`),

  addSupplierToProduct: (productId: string, supplierId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/products/${productId}/warehouse-suppliers`,
      {
        method: 'POST',
        body: JSON.stringify({ supplier_id: supplierId }),
      }
    ),

  removeSupplierFromProduct: (productId: string, supplierId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/products/${productId}/warehouse-suppliers/${supplierId}`,
      {
        method: 'DELETE',
      }
    ),

  // Warehouses
  getWarehouses: () =>
    fetchWithErrorHandling<{
      warehouses: Array<{
        id: string;
        name: string;
        code: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        country: string;
        type: 'standard' | 'cold_storage' | 'distribution';
        status: 'active' | 'inactive';
        capacity: number;
        contact_person: string;
        phone: string;
        email: string;
        created_at: string;
        notes?: string;
      }>;
    }>('/api/warehouses'),

  getWarehouse: (warehouseId: string) =>
    fetchWithErrorHandling<{
      id: string;
      name: string;
      code: string;
      address: string;
      city: string;
      state: string;
      zip_code: string;
      country: string;
      type: 'standard' | 'cold_storage' | 'distribution';
      status: 'active' | 'inactive';
      capacity: number;
      contact_person: string;
      phone: string;
      email: string;
      created_at: string;
      notes?: string;
    }>(`/api/warehouses/${warehouseId}`),

  createWarehouse: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ id: string }>('/api/warehouses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateWarehouse: (warehouseId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/warehouses/${warehouseId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Locations
  getLocations: (warehouseId?: string) => {
    const params = new URLSearchParams();
    if (warehouseId) params.append('warehouse_id', warehouseId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling<{
      locations: Array<{
        id: string;
        warehouse_id: string;
        name: string;
        code: string;
        zone?: string;
        aisle?: string;
        rack?: string | null;
        level?: string | null;
        capacity: number;
        status: 'active' | 'inactive';
        created_at: string;
      }>;
    }>(`/api/locations${query}`);
  },

  getLocation: (locationId: string) =>
    fetchWithErrorHandling<{
      id: string;
      warehouse_id: string;
      name: string;
      code: string;
      zone?: string;
      aisle?: string;
      rack?: string | null;
      level?: string | null;
      capacity: number;
      status: 'active' | 'inactive';
      created_at: string;
    }>(`/api/locations/${locationId}`),

  createLocation: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ id: string }>('/api/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateLocation: (locationId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/locations/${locationId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Batches
  getBatches: () =>
    fetchWithErrorHandling<{
      batches: Array<{
        id: string;
        batch_number: string;
        lot_number?: string | null;
        manufacturing_date?: string;
        expiry_date: string | null;
        expiration_date?: string | null;
        alert_date?: string | null;
        expiry_known?: boolean;
        classification?: 'valid' | 'near_expiry' | 'expired' | 'unknown';
        received_date: string;
        status: 'available' | 'reserved' | 'quarantine' | 'near_expiry' | 'expired';
        notes?: string | null;
        created_at: string;
      }>;
    }>('/api/batches'),

  getBatch: (batchId: string) =>
    fetchWithErrorHandling<{
      id: string;
      batch_number: string;
      lot_number?: string | null;
      manufacturing_date?: string;
      expiry_date: string | null;
      received_date: string;
      status: 'available' | 'reserved' | 'quarantine' | 'near_expiry' | 'expired';
      notes?: string | null;
      created_at: string;
    }>(`/api/batches/${batchId}`),

  createBatch: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ id: string }>('/api/batches', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBatch: (batchId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/api/batches/${batchId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Product-Warehouse-Location Junction
  getProductWarehouseLocations: (filters?: {
    product_id?: string;
    warehouse_id?: string;
    location_id?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', filters.product_id);
    if (filters?.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
    if (filters?.location_id) params.append('location_id', filters.location_id);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling<{
      product_warehouse_locations: Array<{
        id: string;
        product_id: string;
        product_name?: string;
        product_barcode?: string;
        warehouse_id?: string | null;
        warehouse_name?: string;
        location_id: string;
        location_name?: string;
        quantity: number;
      }>;
    }>(`/api/product-warehouse-locations${query}`);
  },

  createProductWarehouseLocation: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ id: string }>('/api/product-warehouse-locations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProductWarehouseLocation: (pwlId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-warehouse-locations/${pwlId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  deleteProductWarehouseLocation: (pwlId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-warehouse-locations/${pwlId}`,
      {
        method: 'DELETE',
      }
    ),

  // Product-Batch Junction
  getProductBatches: (filters?: { product_id?: string; batch_id?: string }) => {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', filters.product_id);
    if (filters?.batch_id) params.append('batch_id', filters.batch_id);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling<{
      product_batches: Array<{
        id: string;
        product_id: string;
        batch_id: string;
        quantity: number;
        warehouse_id?: string;
        location_id?: string;
        warehouse_name?: string;
        location_name?: string;
        batch_number?: string;
        lot_number?: string | null;
        expiry_date?: string | null;
        expiration_date?: string | null;
        alert_date?: string | null;
        expiry_known?: boolean;
        classification?: 'valid' | 'near_expiry' | 'expired' | 'unknown';
        received_date: string;
        status: 'available' | 'reserved' | 'quarantine' | 'near_expiry' | 'expired';
        notes?: string | null;
        created_at: string;
      }>;
    }>(`/api/product-batches${query}`);
  },

  createProductBatch: (data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ id: string }>('/api/product-batches', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProductBatch: (pbId: string, data: Record<string, unknown>) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-batches/${pbId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  deleteProductBatch: (pbId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string; id: string }>(
      `/api/product-batches/${pbId}`,
      {
        method: 'DELETE',
      }
    ),
};

